import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FieldType, Form, FormField, ModalId, useUIActions, Validatable } from "../../../../../features/ui"
import { Operation } from "../../../../operations"
import { DeliveryOrder } from "../../../state/types"
import { CancelItemRow, INVOICE_IN_FLIGHT_MESSAGE, NoteGenerationType } from "../../types"
import { CancelItemsTable } from "../cancel-items-table/cancelItemsTable"
import { deliveryOrderHelpers } from "../../../hooks/deliveryOrderHooks"
import { useActions } from "./actions"
import { TableName } from "../../../../../tables"
import { GenerateNoteModal } from "../../generate-note-modal/generateNoteModal"
import { FiscalDocument, hasSentInvoice } from "../../../../fiscal-documents"
import { HoardOrder, SaleOperation } from "../../../../sales"
import { hoardHelpers } from "../../../../sales/hooks/hoardHooks"
import { toList } from "../../../../../utils/listUtils"

type Props = {
    table: TableName
    operation: Operation
    deliveryOrders: DeliveryOrder[]
    fiscalDocuments: FiscalDocument[]
}

const buildItemRows = (
    operation: Operation,
    deliveryOrders: DeliveryOrder[]
): CancelItemRow[] => {
    const { getPendingQuantity } = deliveryOrderHelpers
    return operation.items
        .map(item => ({
            itemId: item.id,
            productId: item.productId,
            lotId: item.lotId,
            pending: getPendingQuantity(operation, deliveryOrders, item.id),
            cancelQuantity: 0
        }))
        .filter(row => row.pending > 0)
}

export const CancelItemsForm = (props: Props) => {
    const { table, operation, deliveryOrders, fiscalDocuments } = props

    const navigate = useNavigate()

    const defaultRows = useMemo(() => buildItemRows(operation, deliveryOrders), [operation, deliveryOrders])

    const isHoard = table === TableName.HOARD_ORDERS
    const isFinancialHoard = hoardHelpers.isFinancialHoard(table, operation as SaleOperation)
    const hasInvoiceInFlight = isHoard && !!(operation as HoardOrder).invoiceInFlight

    // Ventas: se ofrece el tipo de nota si la factura ya se envió (un preview o una factura rechazada no cuentan).
    const hasInvoice = useMemo(
        () => hasSentInvoice(fiscalDocuments),
        [fiscalDocuments]
    )

    const [itemRows, setItemRows] = useState<CancelItemRow[]>(defaultRows)

    // En acopios la nota fiscal solo corresponde a la cantidad ya facturada de cada ítem.
    const hasInvoicedQuantity = useMemo(
        () => isHoard && hoardHelpers.splitQuantitiesByInvoicing(
            operation as SaleOperation,
            itemRows.map(row => ({ itemId: row.itemId, quantity: row.cancelQuantity })),
            'cancel'
        ).invoiced.length > 0,
        [isHoard, operation, itemRows]
    )

    const hasNoteTypeOption = useMemo(
        () => !hasInvoiceInFlight && !isFinancialHoard && (isHoard ? hasInvoicedQuantity : hasInvoice),
        [hasInvoiceInFlight, isFinancialHoard, isHoard, hasInvoicedQuantity, hasInvoice]
    )

    const { submitCancellation } = useActions()

    const { openModal } = useUIActions()

    useEffect(() => {
        setItemRows(defaultRows)
    }, [defaultRows])

    const totalCancelQuantity = useMemo(
        () => itemRows.reduce((sum, item) => sum + item.cancelQuantity, 0),
        [itemRows]
    )

    const fields: FormField[] = [
        ...(hasInvoiceInFlight ? [{
            name: 'invoiceInFlight',
            type: FieldType.TEXT,
            render: () => <Validatable validations={toList(INVOICE_IN_FLIGHT_MESSAGE)} expanded />
        }] : []),
        {
            name: 'items',
            type: FieldType.TABLE,
            render: () => (
                <CancelItemsTable
                    operation={operation}
                    defaultRows={defaultRows}
                    onChange={setItemRows}
                />
            )
        }
    ]

    const onConfirmNote = async (noteType: NoteGenerationType) => {
        return submitCancellation(table, operation.id, itemRows, noteType)
    }

    const onSubmit = () => {
        if (hasInvoiceInFlight) return
        if (isFinancialHoard) {
            // En financiero solo se anula fiscalmente lo facturado: no se ofrece una nota interna.
            return onConfirmNote(NoteGenerationType.CREDIT_NOTE)
        }
        return hasNoteTypeOption
            ? openModal(ModalId.GENERATE_NOTE)
            : onConfirmNote(NoteGenerationType.ADJUSTMENT_NOTE)
    }

    const onCancel = () => navigate(-1)

    return (
        <>
            <Form
                formId="cancel-items-form"
                fields={fields}
                createLabel="Cancelar Productos"
                disableSubmit={totalCancelQuantity === 0 || hasInvoiceInFlight}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
            {hasNoteTypeOption && <GenerateNoteModal onConfirm={onConfirmNote} />}
        </>
    )
}
